import React, { useEffect, useContext, useState } from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, SafeAreaView } from 'react-native'
import { Button, Text, Icon, Card, Input, Toggle, Divider, Modal, CheckBox, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { addPriceType, updatePriceType, deletePriceType } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { useColors } from '../../utils/useColors'
import { HintView } from '../../components/HintView'
import { useToast } from 'react-native-toast-notifications'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'

export default function PriceTypesScreen({ navigation }) {
  const { t } = useTranslation()
  const colors = useColors()
  const theme = useTheme()
  useContext(UserContext)
  const constants = useAtomValue(constantsAtom)
  const toast = useToast()
  const priceTypesFromAtom = useAtomValue(priceTypesAtom)
  const paymentMethods = constants?.paymentMethods?.data || []
  
  const [priceTypes, setPriceTypes] = useState(() => priceTypesFromAtom)
  const [isLoading, setIsLoading] = useState(false)
  
  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [priceTypeToDelete, setPriceTypeToDelete] = useState(null)
  
  // Form state
  const [formData, setFormData] = useState(null)
  
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={openCreateModal}
          iconName='plus-outline'
        />
      ),
      title: t('settings.priceTypes') || 'Price Types'
    })
  }, [])

  const openCreateModal = () => {
    const newPriceType = {
      id: `priceType_${Date.now()}`, // Auto-generated, not editable by user
      name: '',
      icon: 'pricetags-outline',
      scope: 'store',
      autoRules: {
        rules: [{ type: 'client' }] // Client rule enabled by default
      },
      calculationRules: {
        enabled: false,
        type: 'percentage',
        percentage: 0
      }
    }
    setFormData(newPriceType)
    setEditingIndex(null)
    setShowModal(true)
  }

  const openEditModal = (index) => {
    setFormData({ ...priceTypes[index] })
    setEditingIndex(index)
    setShowModal(true)
  }

  const openDeleteModal = (index) => {
    setPriceTypeToDelete({ index, priceType: priceTypes[index] })
    setShowDeleteModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setFormData(null)
    setEditingIndex(null)
  }

  const handleSaveFromModal = async () => {
    // Validation
    if (!formData.name || formData.name.trim() === '') {
      toast.show(t('settings.priceTypeNameRequired') || 'Price type name is required', { type: 'danger' })
      return
    }

    setIsLoading(true)
    try {
      // Ensure calculation rule values are numbers, not strings
      const cleanedFormData = { ...formData }
      if (cleanedFormData.calculationRules) {
        const cr = cleanedFormData.calculationRules
        if (cr.percentage !== undefined) {
          cr.percentage = typeof cr.percentage === 'string' ? (parseFloat(cr.percentage) || 0) : cr.percentage
        }
        if (cr.multiplier !== undefined) {
          cr.multiplier = typeof cr.multiplier === 'string' ? (parseFloat(cr.multiplier) || 1) : cr.multiplier
        }
        if (cr.amount !== undefined) {
          cr.amount = typeof cr.amount === 'string' ? (parseFloat(cr.amount) || 0) : cr.amount
        }
      }

      let updatedPriceTypes
      if (editingIndex !== null) {
        // Editing existing - keep existing ID
        updatedPriceTypes = [...priceTypes]
        updatedPriceTypes[editingIndex] = cleanedFormData
        // Dual-write (Firestore + Supabase price_types) so the change persists to
        // the Supabase read source, not just transient local state.
        await updatePriceType(cleanedFormData.id, cleanedFormData)
      } else {
        // Creating new - ID already auto-generated in openCreateModal
        updatedPriceTypes = [...priceTypes, cleanedFormData]
        await addPriceType(cleanedFormData)
      }

      setPriceTypes(updatedPriceTypes)
      toast.show(t('settings.priceTypeSaved') || 'Price type saved successfully', { type: 'success' })
      closeModal()
    } catch (error) {
      console.error('Error saving price type:', error)
      toast.show(t('settings.errorSavingPriceType') || 'Error saving price type', { type: 'danger' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      const updatedPriceTypes = priceTypes.filter((_, i) => i !== priceTypeToDelete.index)
      // Dual-write delete so the tier is removed from Supabase price_types too.
      await deletePriceType(priceTypeToDelete.priceType.id)
      setPriceTypes(updatedPriceTypes)
      toast.show(t('settings.priceTypeDeleted') || 'Price type deleted successfully', { type: 'success' })
      setShowDeleteModal(false)
      setPriceTypeToDelete(null)
    } catch (error) {
      console.error('Error deleting price type:', error)
      toast.show(t('settings.errorDeletingPriceType') || 'Error deleting price type', { type: 'danger' })
    } finally {
      setIsLoading(false)
    }
  }

  const updateFormField = (field, value) => {
    setFormData({ ...formData, [field]: value })
  }

  const updateAutoRule = (field, value) => {
    setFormData({
      ...formData,
      autoRules: {
        ...formData.autoRules,
        [field]: value
      }
    })
  }

  // Helper functions for managing multiple auto rules
  const hasRuleType = (ruleType) => {
    return formData?.autoRules?.rules?.some(rule => rule.type === ruleType) || false
  }

  const getRuleByType = (ruleType) => {
    return formData?.autoRules?.rules?.find(rule => rule.type === ruleType)
  }

  const toggleRuleType = (ruleType, defaultRule) => {
    const rules = formData?.autoRules?.rules || []
    const existingIndex = rules.findIndex(rule => rule.type === ruleType)
    
    let newRules
    if (existingIndex >= 0) {
      // Remove the rule
      newRules = rules.filter((_, index) => index !== existingIndex)
    } else {
      // Add the rule
      newRules = [...rules, { type: ruleType, ...defaultRule }]
    }
    
    updateAutoRule('rules', newRules)
  }

  const updateRuleField = (ruleType, field, value) => {
    const rules = formData?.autoRules?.rules || []
    const newRules = rules.map(rule => 
      rule.type === ruleType 
        ? { ...rule, [field]: value }
        : rule
    )
    updateAutoRule('rules', newRules)
  }

  const updateCalculationRule = (field, value) => {
    setFormData({
      ...formData,
      calculationRules: {
        ...formData.calculationRules,
        [field]: value
      }
    })
  }


  const scopeOptions = [
    { id: 'store', text: t('settings.perStore') || 'Per Store' },
    { id: 'organization', text: t('settings.organizationWide') || 'Organization-wide' }
  ]

  const renderPriceTypeCard = (priceType, index) => {
    // Get active rules for display (no longer checking enabled flag)
    const activeRules = priceType.autoRules?.rules?.length > 0
      ? priceType.autoRules.rules
      : []
    
    return (
      <Card style={styles.priceTypeCard} onPress={() => openEditModal(index)}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <Icon
              name={priceType.icon || 'pricetags-outline'}
              style={styles.priceTypeIcon}
            />
            <View style={styles.cardInfo}>
              <Text category='h6' style={styles.priceTypeName}>
                {priceType.name}
              </Text>
              <Text appearance='hint' category='c1'>
                {priceType.scope === 'organization' 
                  ? t('settings.organizationWide') || 'Organization-wide'
                  : t('settings.perStore') || 'Per Store'}
              </Text>
              {priceType.calculationRules?.enabled && (
                <View style={styles.badge}>
                  <Icon name='flash-outline' style={styles.badgeIcon} fill='#FFAA00' />
                  <Text category='c2' appearance='hint'>
                    {t('settings.autoCalculated') || 'Auto-calculated'}
                  </Text>
                </View>
              )}
              {activeRules.length > 0 && (
                <View style={styles.badge}>
                  <Icon name='funnel-outline' style={styles.badgeIcon} fill='#0095FF' />
                  <Text category='c2' appearance='hint'>
                    {activeRules.map(rule => {
                      if (rule.type === 'quantity') return t('settings.quantity') || 'Quantity'
                      if (rule.type === 'payment_method') return t('settings.paymentMethod') || 'Payment'
                      if (rule.type === 'cart_total') return t('settings.cartTotal') || 'Cart Total'
                      if (rule.type === 'client') return t('settings.client') || 'Client'
                      return ''
                    }).join(', ')}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Card>
    )
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.lightGray }]}>
      <ScrollView style={styles.scrollView}>
        <HintView 
          text={t('settings.priceTypesHint') || 'Configure optional additional price types for your products (e.g., wholesale, BNPL, VIP). The default price is set in the product\'s Sale Price field.'}
          iconName='info-outline'
          style={styles.hint}
        />

        {priceTypes.length === 0 ? (
          <Card style={styles.emptyState}>
            <Text category="h6" style={styles.emptyTitle}>
              {t('settings.noPriceTypes') || 'No Price Types'}
            </Text>
            <Text category="p2" style={styles.emptyDescription}>
              {t('settings.createFirstPriceType') || 'Create your first price type to get started'}
            </Text>
          </Card>
        ) : (
          priceTypes.map(renderPriceTypeCard)
        )}

        {/* Edit/Create Modal */}
        <Modal
          visible={showModal}
          style={styles.modal}
          onBackdropPress={closeModal}
          backdropStyle={styles.backdrop}
        >
          <ScrollView style={{flex: 1}} contentContainerStyle={styles.modalContainer}>
            <TouchableOpacity 
              style={{position: 'absolute', top: 10, right: 10, zIndex: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
              onPress={closeModal}
            > 
              <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
            </TouchableOpacity>
            
            <Text category='h6' style={styles.modalTitle}>
              {editingIndex !== null 
                ? t('settings.editPriceType') || 'Edit Price Type'
                : t('settings.newPriceType') || 'New Price Type'}
            </Text>

                {formData && (
                  <>
                    <View style={styles.field}>
                      <Text category='label' style={styles.label}>{t('settings.priceTypeName') || 'Name'}</Text>
                      <Input
                        value={formData.name}
                        onChangeText={(text) => updateFormField('name', text)}
                        placeholder={t('settings.enterName') || 'Enter name'}
                      />
                    </View>

                    <View style={styles.field}>
                      <Text category='label' style={styles.label}>{t('settings.scope') || 'Scope'}</Text>
                      <PlatformDependantSelect
                        selectedOption={scopeOptions.find(o => o.id === formData.scope)}
                        data={scopeOptions}
                        onSelect={(item) => updateFormField('scope', item.id)}
                        style={styles.select}
                      />
                    </View>

                    <Divider style={styles.divider} />
                    
                    <Text category='s1' style={styles.sectionTitle}>{t('settings.priceCalculation') || 'Price Calculation'}</Text>

                    <Toggle
                      checked={formData.calculationRules?.enabled}
                      onChange={(checked) => updateCalculationRule('enabled', checked)}
                      style={styles.toggle}
                    >
                      {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.autoCalculateFromSalePrice') || 'Auto-calculate from sale price'}</Text>}
                    </Toggle>

                    {!formData.calculationRules?.enabled && (
                      <Text appearance='hint' category='c2' style={styles.hintText}>
                        {t('settings.manualPriceHint') || 'If autocalculation is disabled, you should manually set the price for each product'}
                      </Text>
                    )}

                    {formData.calculationRules?.enabled && (
                      <>
                        <View style={styles.field}>
                          <Text category='label' style={styles.label}>{t('settings.calculationType') || 'Calculation Type'}</Text>
                          <PlatformDependantSelect
                            selectedOption={{
                              id: formData.calculationRules?.type || 'percentage',
                              text: formData.calculationRules?.type === 'multiply' ? t('settings.multiply') || 'Multiply' :
                                    formData.calculationRules?.type === 'adjust' ? t('settings.adjust') || 'Adjust' :
                                    t('settings.percentage') || 'Percentage'
                            }}
                            data={[
                              { id: 'percentage', text: t('settings.percentage') || 'Percentage' },
                              { id: 'multiply', text: t('settings.multiply') || 'Multiply' },
                              { id: 'adjust', text: t('settings.adjust') || 'Adjust' }
                            ]}
                            onSelect={(item) => updateCalculationRule('type', item.id)}
                            style={styles.select}
                          />
                        </View>

                        {formData.calculationRules?.type === 'percentage' && (
                          <View style={styles.field}>
                            <Text category='label' style={styles.label}>{t('settings.percentage') || 'Percentage'} (%)</Text>
                            <Input
                              value={formData.calculationRules?.percentage?.toString() || ''}
                              onChangeText={(text) => {
                                // Allow empty string, negative sign, decimal point while typing
                                if (text === '' || text === '-' || text === '.' || text === '-.') {
                                  updateCalculationRule('percentage', text)
                                } else {
                                  const parsed = parseFloat(text)
                                  if (!isNaN(parsed)) {
                                    updateCalculationRule('percentage', text)
                                  }
                                }
                              }}
                              onBlur={() => {
                                // Convert to number on blur
                                const current = formData.calculationRules?.percentage
                                if (typeof current === 'string') {
                                  const parsed = parseFloat(current)
                                  updateCalculationRule('percentage', isNaN(parsed) ? 0 : parsed)
                                }
                              }}
                              keyboardType='numeric'
                              placeholder='10'
                            />
                            <Text appearance='hint' category='c2'>{t('settings.percentageHint') || 'E.g., 10 means +10%, -10 means -10%'}</Text>
                          </View>
                        )}

                        {formData.calculationRules?.type === 'multiply' && (
                          <View style={styles.field}>
                            <Text category='label' style={styles.label}>{t('settings.multiplier') || 'Multiplier'}</Text>
                            <Input
                              value={formData.calculationRules?.multiplier?.toString() || ''}
                              onChangeText={(text) => {
                                // Allow empty string, negative sign, decimal point while typing
                                if (text === '' || text === '-' || text === '.' || text === '-.') {
                                  updateCalculationRule('multiplier', text)
                                } else {
                                  const parsed = parseFloat(text)
                                  if (!isNaN(parsed)) {
                                    updateCalculationRule('multiplier', text)
                                  }
                                }
                              }}
                              onBlur={() => {
                                // Convert to number on blur, default to 1 if invalid
                                const current = formData.calculationRules?.multiplier
                                if (typeof current === 'string') {
                                  const parsed = parseFloat(current)
                                  updateCalculationRule('multiplier', isNaN(parsed) ? 1 : parsed)
                                }
                              }}
                              keyboardType='numeric'
                              placeholder='1.2'
                            />
                            <Text appearance='hint' category='c2'>{t('settings.multiplierHint') || 'E.g., 1.2 means 20% more, 0.8 means 20% less'}</Text>
                          </View>
                        )}

                        {formData.calculationRules?.type === 'adjust' && (
                          <View style={styles.field}>
                            <Text category='label' style={styles.label}>{t('settings.amount') || 'Amount'}</Text>
                            <Input
                              value={formData.calculationRules?.amount?.toString() || ''}
                              onChangeText={(text) => {
                                // Allow empty string, negative sign, decimal point while typing
                                if (text === '' || text === '-' || text === '.' || text === '-.') {
                                  updateCalculationRule('amount', text)
                                } else {
                                  const parsed = parseFloat(text)
                                  if (!isNaN(parsed)) {
                                    updateCalculationRule('amount', text)
                                  }
                                }
                              }}
                              onBlur={() => {
                                // Convert to number on blur
                                const current = formData.calculationRules?.amount
                                if (typeof current === 'string') {
                                  const parsed = parseFloat(current)
                                  updateCalculationRule('amount', isNaN(parsed) ? 0 : parsed)
                                }
                              }}
                              keyboardType='numbers-and-punctuation'
                              placeholder='100'
                            />
                            <Text appearance='hint' category='c2'>{t('settings.adjustHint') || 'Positive to add, negative to subtract (e.g., 100 or -50)'}</Text>
                          </View>
                        )}
                      </>
                    )}

                    <Divider style={styles.divider} />

                    <Text category='s1' style={styles.sectionTitle}>{t('settings.autoRules') || 'Auto-Selection Rules'}</Text>

                    {/* Quantity Rule */}
                    <View style={styles.ruleSection}>
                      <Toggle
                        checked={hasRuleType('quantity')}
                        onChange={() => toggleRuleType('quantity', { minQuantity: 10 })}
                        style={styles.toggle}
                      >
                        {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.quantity') || 'Quantity'}</Text>}
                      </Toggle>
                      
                      {hasRuleType('quantity') && (
                        <View style={styles.ruleDetails}>
                          <Text category='label' style={styles.sublabel}>{t('settings.minQuantity') || 'Minimum Quantity'}</Text>
                          <Input
                            value={getRuleByType('quantity')?.minQuantity?.toString() || ''}
                            onChangeText={(text) => updateRuleField('quantity', 'minQuantity', parseInt(text) || 0)}
                            keyboardType='numeric'
                            placeholder='10'
                            size='small'
                          />
                        </View>
                      )}
                    </View>

                    {/* Payment Method Rule */}
                    <View style={styles.ruleSection}>
                      <Toggle
                        checked={hasRuleType('payment_method')}
                        onChange={() => toggleRuleType('payment_method', { paymentMethodIds: [] })}
                        style={styles.toggle}
                      >
                        {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.paymentMethod') || 'Payment Method'}</Text>}
                      </Toggle>
                      
                      {hasRuleType('payment_method') && (
                        <View style={styles.ruleDetails}>
                          <Text category='label' style={styles.sublabel}>{t('settings.selectPaymentMethods') || 'Select Payment Methods'}</Text>
                          {paymentMethods.map(pm => {
                            const rule = getRuleByType('payment_method')
                            const selectedIds = rule?.paymentMethodIds || []
                            const isChecked = selectedIds.includes(pm.id)
                            
                            return (
                              <CheckBox
                                key={pm.id}
                                checked={isChecked}
                                onChange={(checked) => {
                                  const newIds = checked
                                    ? [...selectedIds, pm.id]
                                    : selectedIds.filter(id => id !== pm.id)
                                  updateRuleField('payment_method', 'paymentMethodIds', newIds)
                                }}
                                style={styles.paymentMethodCheckbox}
                              >
                                {pm.name}
                              </CheckBox>
                            )
                          })}
                        </View>
                      )}
                    </View>

                    {/* Cart Total Rule */}
                    <View style={styles.ruleSection}>
                      <Toggle
                        checked={hasRuleType('cart_total')}
                        onChange={() => toggleRuleType('cart_total', { minAmount: 1000 })}
                        style={styles.toggle}
                      >
                        {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.cartTotal') || 'Cart Total'}</Text>}
                      </Toggle>
                      
                      {hasRuleType('cart_total') && (
                        <View style={styles.ruleDetails}>
                          <Text category='label' style={styles.sublabel}>{t('settings.minCartAmount') || 'Minimum Cart Amount'}</Text>
                          <Input
                            value={getRuleByType('cart_total')?.minAmount?.toString() || ''}
                            onChangeText={(text) => updateRuleField('cart_total', 'minAmount', parseFloat(text) || 0)}
                            keyboardType='numeric'
                            placeholder='1000'
                            size='small'
                          />
                        </View>
                      )}
                    </View>

                    {/* Client Rule - Always enabled, cannot be disabled */}
                    <View style={styles.ruleSection}>
                      <Toggle
                        checked={true}
                        disabled={true}
                        style={styles.toggle}
                      >
                        {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.client') || 'Client'}</Text>}
                      </Toggle>
                      
                      <View style={styles.ruleDetails}>
                        <Text appearance='hint' category='c2'>
                          {t('settings.clientRuleHint') || 'This price will be applied when a client is selected'}
                        </Text>
                      </View>
                    </View>
                  </>
                )}

                <View style={styles.modalActions}>
                  {editingIndex !== null && (
                    <Button
                      status="danger"
                      appearance="ghost"
                      onPress={() => {
                        closeModal()
                        openDeleteModal(editingIndex)
                      }}
                      style={styles.modalButton}
                      disabled={isLoading}
                    >
                      {t('delete') || 'Delete'}
                    </Button>
                  )}
                  <Button
                    appearance="ghost"
                    onPress={closeModal}
                    style={styles.modalButton}
                    disabled={isLoading}
                  >
                    {t('cancel') || 'Cancel'}
                  </Button>
                  <Button
                    onPress={handleSaveFromModal}
                    style={styles.modalButton}
                    disabled={isLoading}
                  >
                    {isLoading ? t('settings.saving') || 'Saving...' : t('save') || 'Save'}
                  </Button>
                </View>
          </ScrollView>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          visible={showDeleteModal}
          style={styles.deleteModal}
          backdropStyle={styles.backdrop}
          onBackdropPress={() => setShowDeleteModal(false)}
        >
          <View style={styles.deleteModalContainer}>
            <TouchableOpacity 
              style={{position: 'absolute', top: 10, right: 10, zIndex: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
              onPress={() => setShowDeleteModal(false)}
            > 
              <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
            </TouchableOpacity>
            
            <Text category='h6' style={styles.modalTitle}>
              {t('settings.deletePriceType') || 'Delete Price Type'}
            </Text>
            <Text category='p1' style={styles.deleteMessage}>
              {t('settings.confirmDeletePriceType') || 'Are you sure you want to delete this price type?'}
              {priceTypeToDelete && (
                <Text category='p1' style={styles.deleteItemName}> &quot;{priceTypeToDelete.priceType.name}&quot;</Text>
              )}
            </Text>
            <View style={styles.deleteModalActions}>
              <Button
                appearance="ghost"
                onPress={() => setShowDeleteModal(false)}
                style={styles.modalButton}
                disabled={isLoading}
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                status="danger"
                onPress={handleDelete}
                style={styles.modalButton}
                disabled={isLoading}
              >
                {isLoading ? t('deleting') || 'Deleting...' : t('delete') || 'Delete'}
              </Button>
            </View>
          </View>
        </Modal>

        <View style={styles.bottomSpacer} />
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 16
  },
  hint: {
    marginBottom: 16
  },
  priceTypeCard: {
    marginBottom: 12
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  priceTypeIcon: {
    width: 32,
    height: 32,
    marginRight: 12
  },
  cardInfo: {
    flex: 1
  },
  priceTypeName: {
    marginBottom: 4
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4
  },
  badgeIcon: {
    width: 12,
    height: 12,
    marginRight: 4
  },
  emptyState: {
    padding: 32,
    alignItems: 'center'
  },
  emptyTitle: {
    marginBottom: 8,
    textAlign: 'center'
  },
  emptyDescription: {
    textAlign: 'center',
    color: '#8F9BB3'
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  modal: {
    top: 100,
    width: '90%',
    maxWidth: 600,
    height: '80%',
    backgroundColor: 'white',
    borderRadius: 20,
  },
  modalContainer: {
    alignItems: 'stretch',
    padding: 32,
  },
  modalTitle: {
    marginBottom: 20
  },
  field: {
    marginBottom: 16
  },
  label: {
    marginBottom: 8
  },
  toggle: {
    marginBottom: 10,
    alignSelf: 'flex-start'
  },
  toggleText: {
    flexShrink: 1
  },
  select: {
    marginTop: 4
  },
  divider: {
    marginVertical: 16
  },
  sectionTitle: {
    marginBottom: 12
  },
  modalActions: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginTop: 16
  },
  modalButton: {
    marginTop: 8
  },
  deleteModal: {
    width: '90%',
    maxWidth: 400,
    backgroundColor: 'white',
    borderRadius: 20,
  },
  deleteModalContainer: {
    padding: 32,
  },
  deleteModalActions: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginTop: 16
  },
  deleteMessage: {
    marginBottom: 16
  },
  deleteItemName: {
    fontWeight: 'bold'
  },
  bottomSpacer: {
    height: 100
  },
  ruleSection: {
    marginBottom: 5
  },
  ruleDetails: {
    marginLeft: 32,
    marginTop: 8,
    marginBottom: 8
  },
  sublabel: {
    marginBottom: 4,
    fontSize: 12
  },
  paymentMethodCheckbox: {
    marginTop: 6,
    marginBottom: 6
  },
  hintText: {
    marginTop: -5,
    marginBottom: 12
  }
})

